import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface GameProps {
  team1_name: string;
  team2_name: string;
  team1_logo: string;
  team2_logo: string;
  team1_score: number;
  team2_score: number;
  match_time: string;
  match_location: string;
  grouping: string;
  status: string;
}

// Helper function to format time from "HH:mm:ss" to "hh:mm AM/PM"
const formatTime = (time: string): string => {
  const [hour, minute] = time.split(':');
  let hours = parseInt(hour);
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format, 0 becomes 12
  return `${hours}:${minute} ${period}`;
};

// Sorting functions
const sortByStatus = (games: GameProps[], status: string) => {
  return games
    .filter(game => game.status === status)
    .sort((b, a) => {
      const getTimeInMillis = (time: string) => {
        const [hour, minute] = time.split(':').map(num => parseInt(num, 10));
        const date = new Date(2024, 0, 1, hour, minute); // Use a fixed date to create Date object
        return date.getTime(); // Get the time in milliseconds
      };

      const timeA = getTimeInMillis(a.match_time);
      const timeB = getTimeInMillis(b.match_time);

      return timeB - timeA; // Ascending order by time in milliseconds
    });
};
const NetballList: React.FC = () => {
  const [games, setGames] = useState<GameProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortStatus, setSortStatus] = useState<string>('default');

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await axios.get('https://tronova.azmiproductions.com/api-aivg/netball-api.php');
        setGames(response.data);
      } catch (error) {
        console.error('Error fetching game data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  const handleSort = (status: string) => {
    setSortStatus(status);
  };

  const getSortedGames = () => {
    if (sortStatus === 'playing') {
      return sortByStatus(games, 'playing');
    } else if (sortStatus === 'completed') {
      return sortByStatus(games, 'completed');
    } else if (sortStatus === 'scheduled') {
      return sortByStatus(games, 'scheduled');
    }
    return games;
  };

  return (
    <div className="relative min-h-screen bg-gray-100 text-white">
      <div className="absolute inset-0">
        <img 
          src="https://aivg.azmiproductions.com/sukas/backsukas.png" 
          alt="Background" 
          className="w-full h-full object-cover opacity-100" 
        />
        <div className="absolute inset-0 bg-black opacity-50"></div>
      </div>


      {/* Main Content */}
      <div className="relative z-10">
        {/* Header with logo and title */}
        <header className=" py-8 shadow-lg">
          <div className="container mx-auto text-center">
            <img
              src="https://azmiproductions.com/assets/img/lokas.png"
              alt="Logo"
              className="mx-auto mb-4 w-24 h-24"
            />
            <h1 className="text-4xl font-extrabold mb-2">SUKAN ANAK SABAH 2.0</h1>
            <p className="text-lg font-light">Netball</p>
          </div>
        </header>

        {/* Sort buttons */}
        <div className="flex justify-center space-x-4">
          <button
            className={`px-4 py-2 mx-2 rounded-lg font-semibold ${sortStatus === 'scheduled' ? 'bg-blue-600 text-black' : 'bg-gray-300 text-gray-900'}`}
            onClick={() => handleSort('scheduled')}
          >
            Scheduled
          </button>
          <button
            className={`px-4 py-2 mx-2 rounded-lg font-semibold ${sortStatus === 'playing' ? 'bg-blue-600 text-black' : 'bg-gray-300 text-gray-900'}`}
            onClick={() => handleSort('playing')}
          >
            Playing
          </button>
          <button
            className={`px-4 py-2 mx-2 rounded-lg font-semibold ${sortStatus === 'completed' ? 'bg-blue-600 text-black' : 'bg-gray-300 text-gray-900'}`}
            onClick={() => handleSort('completed')}
          >
            Completed
          </button>
        </div>

        {/* Game results */}
        <div className="container mx-auto px-6 py-12">
          {loading ? (
            <div className="flex justify-center py-24">
              <img
                src="https://azmiproductions.com/assets/img/lokas.png"
                alt="Loading..."
                className="w-16 h-16"
              />
            </div>
          ) : getSortedGames().length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {getSortedGames().map((game, index) => {
                const matchTimeFormatted = formatTime(game.match_time);

                return (
                  <div
                  key={index}
                  className="rounded-lg overflow-hidden shadow-md transform transition-transform duration-300 hover:scale-105"
                  style={{
                    backgroundImage: 'linear-gradient(135deg, #d19eb5, #e9e9e9)', // Gradient from #33888D to a darker shade
                  }}
                  >
                    {/* Team logos and names */}
                    <div className="flex justify-between items-center mb-4 p-4">
                      <div className="team flex flex-col items-center">
                        <img
                          src={game.team1_logo}
                          alt="Team 1 Logo"
                          className="w-16 h-16 bg-white object-cover rounded-full mb-2 shadow-md"
                        />
                        <p className="font-semibold text-black text-sm">{game.team1_name}</p>
                      </div>
                      
                      <div className="score text-4xl font-extrabold text-black">
                        {game.team1_score} - {game.team2_score}
                      </div>
                      <div className="team flex flex-col items-center">
                        <img
                          src={game.team2_logo}
                          alt="Team 2 Logo"
                          className="w-16 h-16 bg-white object-cover rounded-full mb-2 shadow-md"
                        />
                        <p className="font-semibold text-black text-sm">{game.team2_name}</p>
                      </div>
                    </div>
                    {/* Game details */}
                    <div className="details text-center">
                      <p className="text-lg font-medium text-black">{matchTimeFormatted}</p>
                      <p className="text-sm text-black mb-2">{game.match_location}</p>
                      <span
                        className="inline-block text-sm bg-gray-800 mb-4 text-white font-bold py-1 px-4 rounded-full shadow-md"
                      >
                        {game.grouping} - {game.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-500">No games available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetballList;
